import type { DicomColor } from '~/domain/dicom/dicomColor';
import { DicomStructureSet } from '~/domain/dicom/dicomstructureset';
import type { ContourAssignment } from '~/state/atoms/adaptiveReplanningAtoms';

export interface NamedRoi {
    readonly roiNumber: number;
    readonly name: string;
}

export interface ContourMergePlan {
    // Contour+ ROI number → the planned structure's name.
    readonly targetNames: ReadonlyMap<number, string>;
    // Planned structures whose model structure Contour+ did not return.
    readonly missing: readonly string[];
}

const namesEqual = DicomStructureSet.structureNamesTrimmedEqual;

export function planContourMerge(
    assignments: readonly ContourAssignment[],
    contoured: readonly NamedRoi[]
): ContourMergePlan {
    const targetNames = new Map<number, string>();
    const missing: string[] = [];
    for (const { name, modelRoiName } of assignments) {
        const roi = contoured.find((candidate) =>
            namesEqual(candidate.name, modelRoiName)
        );
        if (roi && !targetNames.has(roi.roiNumber)) {
            targetNames.set(roi.roiNumber, name);
        } else {
            missing.push(name);
        }
    }
    return { targetNames, missing };
}

export function namesMissingFrom(
    expected: readonly string[],
    present: readonly string[]
): string[] {
    return expected.filter(
        (name) => !present.some((held) => namesEqual(held, name))
    );
}

interface ColoredRoiSource {
    readonly roiArray: readonly {
        readonly name: string;
        readonly color: DicomColor;
    }[];
}

// ROI number → the color the planned structure of that name has in the source set.
export function sourceColorsFor(
    targetNames: ReadonlyMap<number, string>,
    source: ColoredRoiSource | undefined
): Map<number, DicomColor> {
    const colors = new Map<number, DicomColor>();
    for (const [roiNumber, name] of targetNames) {
        const roi = source?.roiArray.find((candidate) =>
            namesEqual(candidate.name, name)
        );
        if (roi) {
            colors.set(roiNumber, { ...roi.color });
        }
    }
    return colors;
}
